import { inject, Injectable } from '@angular/core'
import {
  filter,
  firstValueFrom,
  map,
  Observable,
  pairwise,
  scan,
  shareReplay,
  startWith,
  Subject,
  tap,
} from 'rxjs'
import { FilterParams } from '../../utilities/http-params'
import { ServicePointGroupService } from '../../../public/service-point-groups/service-point-group.service'
import { ActivatedRoute, Router } from '@angular/router'
import { Location } from '@angular/common'
import { DashboardConfig, DashboardConfigPageParam } from '../../models/dashboardConfig'
import { EntityFilter, EntityFilterAction } from './entity-filter'
import { FlagFilter, FlagFilterAction } from './flag-filter'
import { ServicePointSearchFilter, ServicePointSearchFilterAction } from './service-point-search-filter'
import { ServicePointGroupFilter, ServicePointGroupFilterAction } from './service-point-group-filter'
import { StringFilter, StringFilterAction } from './string-filter'
import { ServicePointFilter, ServicePointFilterAction } from './service-point-filter'
import { DateRangeFilter, DateRangeFilterAction } from './date-range-filter'
import { QueryParam, ActiveFilterDisplay } from './common'
import { NumberRangeFilter, NumberRangeFilterAction } from './number-range-filter'
import { isEmpty } from 'lodash'
import { ReferenceDataSource } from '../../../public/reference-data/base-reference.service'
import { EntityRef } from '../../models/referenceData'
import { ReferenceDataService } from '../../../public/reference-data/reference-data.service'
import { ServicePointGroupWithRefs } from '../../../public/service-point-groups/service-point-group.types'

export type Filter =
  | DateRangeFilter
  | EntityFilter
  | FlagFilter
  | NumberRangeFilter
  | ServicePointSearchFilter
  | ServicePointFilter
  | ServicePointGroupFilter
  | StringFilter

export type FilterGroupState = Readonly<Record<string, Filter>>

export type FilterState = Readonly<Record<string, FilterGroupState>>

export type InitStateAction = Readonly<{
  type: 'INIT_STATE_ACTION'
}>

export type ClearFiltersAction = Readonly<{
  type: 'CLEAR_FILTERS_ACTION',
  payload: { fieldGroup: string },
}>

export type InitFiltersAction = Readonly<{
  type: 'INIT_FILTERS_ACTION',
  payload: {
    fieldGroup: string,
    fields: Record<string, Filter>
  }
}>

type OwnActions =
  | InitStateAction
  | InitFiltersAction
  | ClearFiltersAction

export type FilterAction =
  | OwnActions
  | DateRangeFilterAction
  | EntityFilterAction
  | FlagFilterAction
  | NumberRangeFilterAction
  | ServicePointSearchFilterAction
  | ServicePointFilterAction
  | ServicePointGroupFilterAction
  | StringFilterAction


const mapFilter = <F extends Filter>(
  state: FilterState,
  fieldGroup: string,
  fieldName: string,
  fn: (filter: F) => F,
): FilterState  => {
  const filter = state[fieldGroup]?.[fieldName]
  return filter
    ? {
      ...state,
      [fieldGroup]: {
        ...state[fieldGroup],
        [fieldName]: fn(filter as F),
      },
    }
    : state
}

export const filterReducer = (
  state: FilterState,
  action: FilterAction,
): FilterState => {
  switch (action.type) {
    case 'INIT_STATE_ACTION':
      return state ?? {}
    case 'INIT_FILTERS_ACTION':
      return {
        ...state,
        [action.payload.fieldGroup]: action.payload.fields,
      }
    case 'CLEAR_FILTERS_ACTION':
      return Object.entries(state)
        .reduce((s, [k, v]) =>
          k === action.payload.fieldGroup ? s :({ ...s, [k]: v }), {},
        )
    case 'SET_DATE_RANGE_FILTER_CONFIG_ACTION':
    case 'SET_DATE_RANGE_FILTER_VALUE_ACTION':
      return mapFilter<DateRangeFilter>(
        state,
        action.meta.fieldGroup,
        action.meta.fieldName,
        (fltr: DateRangeFilter) => fltr.apply(action))

    case 'SET_ENTITY_FILTER_CONFIG_ACTION':
    case 'ADD_ENTITY_REFS_ACTION':
    case 'REMOVE_ENTITY_REFS_ACTION':
    case 'SET_ENTITY_REFS_ACTION':
    case 'ADD_ENTITY_WILDCARDS_ACTION':
    case 'REMOVE_ENTITY_WILDCARDS_ACTION':
    case 'SET_ENTITY_WILDCARDS_ACTION':
      return mapFilter<EntityFilter>(
        state,
        action.meta.fieldGroup,
        action.meta.fieldName,
        (fltr: EntityFilter) => fltr.apply(action))

    case 'SET_FLAG_FILTER_CONFIG_ACTION':
    case 'SET_FLAG_FILTER_VALUE_ACTION':
      return mapFilter<FlagFilter>(
        state,
        action.meta.fieldGroup,
        action.meta.fieldName,
        (fltr: FlagFilter) => fltr.apply(action))

    case 'SET_NUMBER_RANGE_FILTER_CONFIG_ACTION':
    case 'SET_NUMBER_RANGE_FILTER_VALUE_ACTION':
      return mapFilter<NumberRangeFilter>(
        state,
        action.meta.fieldGroup,
        action.meta.fieldName,
        (fltr: NumberRangeFilter) => fltr.apply(action))

    case 'SET_SERVICE_POINT_SEARCH_FILTER_CONFIG_ACTION':
    case 'SET_SERVICE_POINT_SEARCH_FILTER_VALUE_ACTION':
      return mapFilter<ServicePointSearchFilter>(
        state,
        action.meta.fieldGroup,
        action.meta.fieldName,
        (fltr: ServicePointSearchFilter) => fltr.apply(action))

    case 'SET_SERVICE_POINT_FILTER_CONFIG_ACTION':
    case 'ADD_SERVICE_POINTS_ACTION':
    case 'REMOVE_SERVICE_POINTS_ACTION':
    case 'SET_SERVICE_POINTS_ACTION':
      return mapFilter<ServicePointFilter>(
        state,
        action.meta.fieldGroup,
        action.meta.fieldName,
        (fltr: ServicePointFilter) => fltr.apply(action))

    case 'SET_SERVICE_POINT_GROUP_FILTER_CONFIG_ACTION':
    case 'ADD_SERVICE_POINT_GROUPS_ACTION':
    case 'REMOVE_SERVICE_POINT_GROUPS_ACTION':
    case 'SET_SERVICE_POINT_GROUPS_ACTION':
      return mapFilter<ServicePointGroupFilter>(
        state,
        action.meta.fieldGroup,
        action.meta.fieldName,
        (fltr: ServicePointGroupFilter) => fltr.apply(action))

    case 'SET_STRING_FILTER_CONFIG_ACTION':
    case 'ADD_STRINGS_ACTION':
    case 'REMOVE_STRINGS_ACTION':
    case 'SET_STRINGS_ACTION':
      return mapFilter<StringFilter>(
        state,
        action.meta.fieldGroup,
        action.meta.fieldName,
        (fltr: StringFilter) => fltr.apply(action))

    default:
      return state
  }
}

type ServicePointGroupEntityFilter = Readonly<{
  field: keyof ServicePointGroupWithRefs
  resource: ReferenceDataSource
  fieldDisplay: string
}>

const servicePointGroupEntityFilters: readonly ServicePointGroupEntityFilter[] = [
  { field: 'control_area_uid', resource: 'controlAreas', fieldDisplay: 'Control Area' },
  { field: 'delivery_class_uid', resource: 'deliveryClasses', fieldDisplay: 'Delivery Class' },
  { field: 'eldc_uid', resource: 'localDistributionCompanies', fieldDisplay: 'ELDC' },
  { field: 'ldc_uid', resource: 'localDistributionCompanies', fieldDisplay: 'LDC' },
  { field: 'location_uid', resource: 'locations', fieldDisplay: 'Location' },
  { field: 'loss_class_uid', resource: 'lossClasses', fieldDisplay: 'Loss Class' },
  { field: 'meter_type_uid', resource: 'meterTypes', fieldDisplay: 'Meter Type' },
  { field: 'profile_class_uid', resource: 'profileClasses', fieldDisplay: 'Profile Class' },
  { field: 'retailer_uid', resource: 'retailers', fieldDisplay: 'Retailer' },
  { field: 'service_point_source_uid', resource: 'servicePointSources', fieldDisplay: 'Service Point Source' },
  { field: 'supply_class_uid', resource: 'supplyClasses', fieldDisplay: 'Supply Class' },
  { field: 'cpnode_uid', resource: 'cpNodes', fieldDisplay: 'CP Nodes' },
]

@Injectable({ providedIn: 'root' })
export class FilterService {
  private readonly servicePointGroupService = inject(ServicePointGroupService)
  private readonly referenceDataService = inject(ReferenceDataService)

  private readonly action$ = new Subject<FilterAction>()

  private readonly state$ = this.action$
    .asObservable()
    .pipe(
      startWith(FilterService.InitStateAction()),
      scan(filterReducer, {}),
      shareReplay(1),
    )

  private static InitStateAction(): InitStateAction {
    return { type: 'INIT_STATE_ACTION' }
  }

  static ClearFiltersAction(payload: ClearFiltersAction['payload']): ClearFiltersAction {
    return { type: 'CLEAR_FILTERS_ACTION', payload }
  }

  static InitFiltersAction(payload: InitFiltersAction['payload']): InitFiltersAction {
    return { type: 'INIT_FILTERS_ACTION', payload }
  }

  constructor() {
    // need a subscriber to put things in motion
    // no need to do any cleanup though since we own the state
    this.state$.subscribe()
  }

  readonly dispatch = (action: FilterAction) => {
    this.action$.next(action)
  }

  groupState(fieldGroup: string): Observable<FilterGroupState> {
    return this.state$.pipe(map(s => s[fieldGroup] ?? {}))
  }

  state(fieldGroup: string): Observable<FilterGroupState> {
    return this.groupState(fieldGroup).pipe(filter(s => !isEmpty(s)))
  }

  importDashboardParams(dashboard: DashboardConfig) {
    const fieldGroup = dashboard.name
    const { dispatch } = this
    dispatch(FilterService.ClearFiltersAction({ fieldGroup }))

    const toFilter = (p: DashboardConfigPageParam): Filter | undefined => {
      const meta = { fieldGroup, fieldName: p.widget, fieldDisplay: p.display }
      switch(p.config.type) {
        case 'DATE_RANGE':
          return new DateRangeFilter(
            {
              config: {
                showOperation: p.config.show_operation,
                showRange: p.config.show_range,
                showTime: p.config.show_time,
                showSeconds: p.config.show_seconds,
                sendTime: p.config.send_time,
                onlyMonth: p.config.only_month,
                constant: p.constant,
                meta,
              },
            },
            dispatch,
          )
        case 'ENTITY':
          return new EntityFilter(
            {
              config: {
                allowMultiple: p.config.allow_multiple,
                allowWildcards: p.config.allow_wildcards,
                resource: p.config.resource,
                constant: p.constant,
                meta,
              },
            },
            dispatch,
          )
        case 'FLAG':
          return new FlagFilter({ config: { constant: p.constant, meta } }, dispatch)
        case 'NUMBER_RANGE':
          return new NumberRangeFilter(
            { config: { constant: p.constant, meta, allowRange: p.config.allow_range, precision: p.config.precision } },
            dispatch,
          )
        case 'SERVICE_POINT':
          return new ServicePointFilter({ config: { allowMultiple: true, constant: p.constant, meta } }, dispatch)
        case 'SERVICE_POINT_GROUP':
          return new ServicePointGroupFilter(
            { config: { allowMultiple: p.config.allow_multiple, constant: p.constant, meta } },
            dispatch,
          )
        case 'STRING':
          return new StringFilter(
            { config: { allowMultiple: p.config.allow_multiple, constant: p.constant, meta } },
            dispatch,
          )
        default:
          return undefined
      }
    }

    const filters = dashboard.params.map(toFilter).filter((f): f is Filter => Boolean(f))

    const fields: Record<string, Filter> = {}
    filters.forEach(f => {
      fields[f.state.config.meta.fieldName] = f
    })
    dispatch(FilterService.InitFiltersAction({ fieldGroup, fields }))
  }

  async importServicePointGroup(group: ServicePointGroupWithRefs) {
    await this.importServicePointGroupFilters(group.service_point_group_uid, group)
  }

  async importServicePointGroupFilters(fieldGroup: string, group: Partial<ServicePointGroupWithRefs>) {
    const fields: Record<string, Filter> = {
      service_point_id_search: new ServicePointSearchFilter(
        {
          value: group.service_point_id_search ?? null,
          config: {
            meta: {
              fieldGroup,
              fieldName: 'service_point_id_search',
              fieldDisplay: 'Service Point',
            },
          },
        },
        this.dispatch,
      ),
      service_point_group_name: new StringFilter(
        {
          values: [group.service_point_group_name ?? ''],
          config: {
            allowMultiple: false,
            meta: {
              fieldGroup,
              fieldName: 'service_point_group_name',
              fieldDisplay: 'Group Name',
            },
          },
        },
        this.dispatch,
      ),
      company_code: new EntityFilter(
        {
          entities: group.company_code ?? [],
          config: {
            resource: 'companies',
            allowMultiple: true,
            meta: {
              fieldGroup,
              fieldName: 'company_code',
              fieldDisplay: 'Company',
            },
          },
        },
        this.dispatch,
      ),
      service_point_uid: new ServicePointFilter(
        {
          servicePoints: group.service_point_uid ?? [],
          config: {
            allowMultiple: true,
            meta: {
              fieldGroup,
              fieldName: 'service_point_uid',
              fieldDisplay: 'Service Point',
            },
          },
        },
        this.dispatch,
      ),
    }

    servicePointGroupEntityFilters.forEach(({ field, resource, fieldDisplay }) => {
      fields[field] = new EntityFilter(
        {
          entities: (group[field] ?? []) as EntityRef[],
          config: {
            resource,
            allowMultiple: true,
            meta: {
              fieldGroup,
              fieldName: field,
              fieldDisplay,
            },
          },
        },
        this.dispatch,
      )
    })

    this.dispatch(FilterService.InitFiltersAction({ fieldGroup, fields }))
  }

  async manageQueryParms(
    router: Router,
    route: ActivatedRoute,
    location: Location,
    fieldGroup: string,
    defaultValues: Record<string, unknown> = {},
  ): Promise<Observable<unknown>> {

    const queryParams = await firstValueFrom(route.queryParams.pipe(
      map(qp => Object.entries(qp).map(([name, value]: string[]) => ({ name, value }))),
    ))
    await this.fromURLQueryParams(fieldGroup, queryParams, defaultValues)

    return this.asUrlQueryParams(fieldGroup)
      .pipe(
        startWith([] as QueryParam[]),
        pairwise(),
        tap(([lastQp, nextQp]) => {
          const urlTree = router.createUrlTree([], {
            relativeTo: route,
            queryParams: nextQp.reduce(
              (acc, { name, value }) => ({ ...acc, [name]: value }),
              lastQp.reduce((acc, next) => ({ ...acc, [next.name]: null }), {}),
            ),
            queryParamsHandling: 'merge',
          })
          location.go(urlTree.toString())
        }),
      )
  }

  private getSelectedFields = (fieldName?: string | readonly string[]): readonly string[] => {
    if (typeof fieldName === 'string') {
      return [fieldName]
    } else if (Array.isArray(fieldName)) {
      return fieldName
    } else {
      return []
    }
  }

  private getFilters(fieldGroup: string, fieldName?: string | readonly string[]): Observable<readonly Filter[]> {
    const selectedFields = this.getSelectedFields(fieldName)
    return this.state$.pipe(
      map(state => {
        const result = Object.values(state[fieldGroup] ?? {})
        return selectedFields.length === 0 ? result : result.filter(f => selectedFields.includes(f.state.config.meta.fieldName))
      }),
    )
  }


  asParams(fieldGroup: string, fieldName?: string | readonly string[]): Observable<FilterParams[]> {
    return this.getFilters(fieldGroup, fieldName).pipe(
      map(filters => filters.map(_ => _.asParams()).flat()),
    )
  }

  asActiveDisplay(fieldGroup: string, fieldName?: string | readonly string[]): Observable<ActiveFilterDisplay[]> {
    return this.getFilters(fieldGroup, fieldName).pipe(
      map(filters => filters.map(_ => _.asActiveDisplay()).flat()),
    )
  }

  asCount(fieldGroup: string, fieldName?: string | string[]): Observable<number> {
    return this.getFilters(fieldGroup, fieldName).pipe(
      map(filters => filters.reduce((sum, filter) => sum + filter.asCount(), 0)),
    )
  }

  asUrlQueryParams(fieldGroup: string, fieldName?: string | readonly string[]): Observable<QueryParam[]> {
    return this.getFilters(fieldGroup, fieldName).pipe(
      map(filters => filters.map(_ => _.asUrlQueryParams()).flat()),
    )
  }

  async fromURLQueryParams(
    fieldGroup: string,
    queryParams: readonly QueryParam[],
    defaultValues: Record<string, unknown> = {},
  ) {
    const filters = Object.values(await firstValueFrom(this.groupState(fieldGroup))) ?? []

    const importFilter = async (f: Filter) => {
      switch(f.type) {
        case 'DATE_RANGE':
          f.fromUrlQueryParams(
            queryParams,
            defaultValues[f.state.config.meta.fieldName] as Parameters<DateRangeFilter['fromUrlQueryParams']>[1],
          )
          break
        case 'ENTITY': {
          const resource = f.state.config.resource
          const entities = resource
            ? await firstValueFrom(this.referenceDataService.data(resource))
            : []
          f.fromUrlQueryParams(
            queryParams,
            entities,
            defaultValues[f.state.config.meta.fieldName] as Parameters<EntityFilter['fromUrlQueryParams']>[2],
          )
          break
        }
        case 'FLAG':
          f.fromUrlQueryParams(
            queryParams,
            defaultValues[f.state.config.meta.fieldName] as Parameters<FlagFilter['fromUrlQueryParams']>[1],
          )
          break
        case 'NUMBER_RANGE':
          f.fromUrlQueryParams(
            queryParams,
            defaultValues[f.state.config.meta.fieldName] as Parameters<NumberRangeFilter['fromUrlQueryParams']>[1],
          )
          break
        case 'SERVICE_POINT_SEARCH':
          f.fromUrlQueryParams(
            queryParams,
            defaultValues[f.state.config.meta.fieldName] as Parameters<ServicePointSearchFilter['fromUrlQueryParams']>[1],
          )
          break
        case 'SERVICE_POINT': {
          const fieldName = f.state.config.meta.fieldName
          const uids = queryParams.find(qp => qp.name === fieldName)?.value.split(',') ?? []
          const servicePoints = await firstValueFrom(this.referenceDataService.servicePoints(
            { pageNumber: 1, pageSize: uids.length || 1 },
            [{ name: 'service_point_uid', values: [{ value: uids.join(','), operation: 'in=' }] }],
          ))
          f.fromUrlQueryParams(
            queryParams,
            servicePoints,
            defaultValues[f.state.config.meta.fieldName] as Parameters<ServicePointFilter['fromUrlQueryParams']>[2],
          )
          break
        }
        case 'SERVICE_POINT_GROUP': {
          const groups = await firstValueFrom(this.servicePointGroupService.get( ))
          f.fromUrlQueryParams(
            queryParams,
            groups,
            defaultValues[f.state.config.meta.fieldName] as Parameters<ServicePointGroupFilter['fromUrlQueryParams']>[2],
          )
          break
        }
        case 'STRING':
          f.fromUrlQueryParams(
            queryParams,
            defaultValues[f.state.config.meta.fieldName] as Parameters<ServicePointSearchFilter['fromUrlQueryParams']>[1],
          )
          break
      }
    }

    filters.forEach(f => {
      void importFilter(f)
    })
  }

  asServicePointGroupWithRefs(fieldGroup: string): Observable<Partial<ServicePointGroupWithRefs>> {
    return this.state$.pipe(
      map((state) => {
        return Object.values(state[fieldGroup] ?? {})
          .reduce((acc, filter): Partial<ServicePointGroupWithRefs> => {
            return { ...acc, ...filter.asServicePointGroupWithRefs() }
          }, {})
      }),
    )
  }
}
